<script setup>
import { ref, computed, defineProps, defineEmits, onMounted, watch } from "vue";
import GlobalDialog from "@/components/layout/GlobalDialog.vue";
import ECombobox from "@/components/element-components/ECombobox";
import ExportButton from "@/components/new-home/summary/components/ExportButton.vue";
import { useCompletedContentStore } from "@/stores/home/useCompletedContent.store";
import { storeToRefs } from "pinia";
import LoadingState from "@/components/partials/LoadingState.vue";
import { useRouter } from "vue-router";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const router = useRouter();
const emit = defineEmits(["update:modelValue"]);
const openTab = ref(1);
const sortBy = ref("newest");

const completedContentStore = useCompletedContentStore();
const { units, courses, modules, playlists, loadingContent } =
  storeToRefs(completedContentStore);

const showDialog = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit("update:modelValue", newValue);
  },
});

const sorting = (contents, sortValue) => {
  if (!contents || contents.length === 0) return;

  const sortData = [...contents];

  sortData.sort((a, b) => {
    switch (sortValue) {
      case "a-z":
        if (openTab.value === 1) {
          return a.unit?.name.localeCompare(b.unit?.name);
        }
        return a.name?.localeCompare(b.name);
      case "z-a":
        if (openTab.value === 1) {
          return b.unit?.name.localeCompare(a.unit?.name);
        }
        return b.name?.localeCompare(a.name);
      case "newest":
        return new Date(b.completed_at) - new Date(a.completed_at);
      case "oldest":
        return new Date(a.completed_at) - new Date(b.completed_at);
      case "recent-completion":
        return new Date(b.completed_at) - new Date(a.completed_at);
      case "oldest-completion":
        return new Date(a.completed_at) - new Date(b.completed_at);
    }
  });

  switch (openTab.value) {
    case 1:
      completedContentStore.updateUnits(sortData);
      break;
    case 2:
      completedContentStore.updateModules(sortData);
      break;
    case 3:
      completedContentStore.updateCourses(sortData);
      break;
    case 4:
      completedContentStore.updatePlaylists(sortData);
      break;
  }
};

const sorts = ref([
  {
    name: "A-Z",
    value: "a-z",
  },
  {
    name: "Z-A",
    value: "z-a",
  },
  {
    name: "Newest to Oldest",
    value: "newest",
  },
  {
    name: "Oldest to Newest",
    value: "oldest",
  },
]);

const filteredUnitsList = computed(() => {
  return units.value ?? [];
});

const filteredModuleList = computed(() => {
  return modules.value ?? [];
});

const filteredCourseList = computed(() => {
  return courses.value ?? [];
});

const filteredPlaylist = computed(() => {
  return playlists.value ?? [];
});

const toggles = computed(() => {
  return [
    { id: 1, name: "Units", count: filteredUnitsList.value.length },
    { id: 2, name: "Modules", count: filteredModuleList.value.length },
    { id: 3, name: "Courses", count: filteredCourseList.value.length },
    { id: 4, name: "Playlist", count: filteredPlaylist.value.length },
  ];
});

function changeSort(sortValue) {
  if (!sortValue) return;

  console.log("open tab: ", openTab.value);

  switch (openTab.value) {
    case 1:
      sorting(filteredUnitsList.value, sortValue);
      break;
    case 2:
      sorting(filteredModuleList.value, sortValue);
      break;
    case 3:
      sorting(filteredCourseList.value, sortValue);
      break;
    case 4:
      sorting(filteredPlaylist.value, sortValue);
      break;
  }
}

const handleToggleTabs = (tab) => {
  openTab.value = tab;
};

function goToUnit(unitId, playlistId = null, playlistType = "") {
  if (playlistId) {
    if (playlistType === "custom") {
      router.push(`/custom-playlist/${unitId}/${playlistId}`);
    } else {
      router.push(`/units/${unitId}/${playlistId}`);
    }
    return;
  }

  router.push(`/units/${unitId}`);
}

onMounted(() => {
  completedContentStore.handleFetchCompletedContent();
});

watch(sortBy, (val) => {
  changeSort(val);
});

watch(
  playlists,
  (newValue) => {
    console.log("Playlists changed:", newValue);
  },
  { deep: true }
);

watch(openTab, () => {
  changeSort(sortBy.value);
});
</script>

<template>
  <GlobalDialog
    :titleCenter="false"
    dialogDefaultSize="70%"
    v-model="showDialog"
    title=""
    :showClose="false"
    :showActionButton="false"
    forceClose

  >
    <template #content>
      <div class="relative text-left overflow-hidden transform transition-all w-full">
        <!-- Header -->
        <div
          class="flex items-start justify-between gap-4 pb-4 border-b border-untitled-gray-100"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-xl bg-app-primary/10 text-app-primary shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-untitled-gray-900">
                Completed Contents
              </h3>
              <p class="text-sm text-untitled-gray-500 mt-0.5">
                Everything you've finished across units, modules, courses,
                and playlists.
              </p>
            </div>
          </div>
          <button
            type="button"
            @click="showDialog = false"
            class="shrink-0 text-untitled-gray-400 hover:text-untitled-gray-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Toolbar: tabs + sort + export -->
        <div
          class="flex flex-col gap-3 py-4 border-b border-untitled-gray-100 md:flex-row md:items-center md:justify-between"
        >
          <div
            class="inline-flex flex-wrap items-center gap-1 rounded-lg bg-untitled-gray-50 p-1 w-full md:w-auto"
          >
            <button
              v-for="toggle in toggles"
              :key="toggle.id"
              type="button"
              @click="handleToggleTabs(toggle.id)"
              class="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap"
              :class="
                openTab === toggle.id
                  ? 'bg-white text-app-primary shadow-sm'
                  : 'text-untitled-gray-500 hover:text-untitled-gray-700'
              "
            >
              {{ toggle.name }}
              <span
                class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[11px] font-semibold"
                :class="
                  openTab === toggle.id
                    ? 'bg-app-primary/10 text-app-primary'
                    : 'bg-untitled-gray-200 text-untitled-gray-500'
                "
              >
                {{ toggle.count }}
              </span>
            </button>
          </div>

          <div class="flex items-center gap-2 w-full md:w-auto">
            <div class="flex items-center gap-2 w-full sm:w-auto">
              <label
                class="text-xs font-medium text-untitled-gray-500 whitespace-nowrap"
                >Sort by</label
              >
              <div class="w-full sm:w-[190px]">
                <ECombobox
                  v-model="sortBy"
                  name="sorts"
                  :options="sorts"
                  option-name="name"
                  value-key="value"
                  placeholder="Sort By"
                  :multiple="false"
                  :filterable="false"
                  :clearable="false"
                />
              </div>
            </div>
            <ExportButton />
          </div>
        </div>

        <!-- Content -->
        <div class="pt-4">
          <!-- Units Tab -->
          <div :class="{ hidden: openTab !== 1, block: openTab === 1 }">
            <LoadingState v-if="loadingContent" class="mx-auto text-center py-12" />
            <div v-else>
              <ul
                v-if="filteredUnitsList.length > 0"
                role="list"
                class="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 grid-cols-1 gap-4 max-h-[520px] overflow-y-auto pr-1"
              >
                <li
                  class="flex flex-col rounded-lg border border-untitled-gray-200 bg-white hover:border-app-primary/40 hover:shadow-md transition-all"
                  v-for="data in filteredUnitsList"
                  :key="data.unit?.id"
                >
                  <div class="relative flex-shrink-0">
                    <img
                      v-if="data.thumbnail?.includes('google')"
                      class="w-full h-40 object-cover"
                      v-lazy="{
                        src: 'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        error:
                          'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        loading:
                          'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
                      }"
                    />
                    <img
                      v-else-if="data?.thumbnail"
                      v-lazy="{
                        src: data?.thumbnail,
                        error:
                          'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        loading:
                          'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
                      }"
                      class="w-full h-40 object-cover"
                    />
                    <img
                      v-else
                      src="https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg"
                      class="w-full h-40 object-cover"
                    />
                    <div
                      class="absolute top-2 right-2 inline-flex items-center gap-1 bg-app-primary text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        class="w-3 h-3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      Completed
                    </div>
                  </div>

                  <div class="flex flex-col flex-1 p-4 gap-3">
                    <h4 class="text-sm font-semibold text-untitled-gray-800 leading-snug">
                      <a
                        href="#"
                        @click.prevent="goToUnit(data.unit_id)"
                        class="hover:text-app-primary transition-colors"
                      >
                        {{ data.unit?.name }}
                      </a>
                    </h4>

                    <p
                      v-if="data.completed_at"
                      class="inline-flex items-center gap-1.5 text-xs text-untitled-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-3.5 h-3.5 text-untitled-gray-400"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                        />
                      </svg>
                      Completed {{ new Date(data.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }}
                    </p>

                    <div
                      class="mt-auto pt-3 border-t border-untitled-gray-100 flex items-center justify-end"
                    >
                      <button
                        @click="goToUnit(data.unit_id)"
                        type="button"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-3 h-3"
                        >
                          <path
                            stroke-linecap="round"
                            d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                          />
                        </svg>
                        View Unit
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-untitled-gray-400"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="w-10 h-10"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                <p class="mt-3 text-sm font-medium text-untitled-gray-500">
                  No completed units found.
                </p>
              </div>
            </div>
          </div>

          <!-- Modules Tab -->
          <div :class="{ hidden: openTab !== 2, block: openTab === 2 }">
            <LoadingState v-if="loadingContent" class="mx-auto text-center py-12" />
            <div v-else>
              <ul
                v-if="filteredModuleList.length > 0"
                role="list"
                class="grid lg:grid-cols-2 xl:grid-cols-3 grid-cols-1 gap-4 max-h-[520px] overflow-y-auto pr-1"
              >
                <li
                  class="flex flex-col rounded-lg border border-untitled-gray-200 bg-white hover:border-app-primary/40 hover:shadow-md transition-all"
                  v-for="module in filteredModuleList"
                  :key="module.module_id"
                >
                  <div class="relative flex-shrink-0">
                    <img
                      v-if="module?.thumbnail"
                      v-lazy="{
                        src: module.thumbnail,
                        error:
                          'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        loading:
                          'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
                      }"
                      class="w-full h-40 object-cover"
                    />
                    <img
                      v-else
                      src="https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg"
                      class="w-full h-40 object-cover"
                    />
                    <div
                      class="absolute top-2 right-2 inline-flex items-center gap-1 bg-app-primary text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        class="w-3 h-3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      Completed
                    </div>
                  </div>

                  <div class="flex flex-col flex-1 p-4 gap-3">
                    <h4 class="text-sm font-semibold text-untitled-gray-800 leading-snug">
                      <a
                        href="#"
                        @click.prevent="goToUnit(module.first_unit_id)"
                        class="hover:text-app-primary transition-colors"
                        >{{ module.name }}</a
                      >
                    </h4>

                    <p
                      v-if="module.completed_at"
                      class="inline-flex items-center gap-1.5 text-xs text-untitled-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-3.5 h-3.5 text-untitled-gray-400"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                        />
                      </svg>
                      Completed {{ new Date(module.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }}
                    </p>

                    <div
                      class="mt-auto pt-3 border-t border-untitled-gray-100 flex items-center justify-end"
                    >
                      <button
                        @click="goToUnit(module.first_unit_id)"
                        type="button"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-3 h-3"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                          />
                        </svg>
                        View Module
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-untitled-gray-400"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="w-10 h-10"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
                  />
                </svg>
                <p class="mt-3 text-sm font-medium text-untitled-gray-500">
                  No completed modules found.
                </p>
              </div>
            </div>
          </div>

          <!-- Courses Tab -->
          <div :class="{ hidden: openTab !== 3, block: openTab === 3 }">
            <LoadingState v-if="loadingContent" class="mx-auto text-center py-12" />
            <div v-else>
              <ul
                v-if="filteredCourseList.length > 0"
                role="list"
                class="grid lg:grid-cols-2 xl:grid-cols-3 grid-cols-1 gap-4 max-h-[520px] overflow-y-auto pr-1"
              >
                <li
                  class="flex flex-col rounded-lg border border-untitled-gray-200 bg-white hover:border-app-primary/40 hover:shadow-md transition-all"
                  v-for="course in filteredCourseList"
                  :key="course.course_id"
                >
                  <div class="relative flex-shrink-0">
                    <img
                      v-if="course.thumbnail"
                      class="w-full h-40 object-cover"
                      v-lazy="{
                        src: course.thumbnail,
                        error:
                          'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        loading:
                          'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
                      }"
                      alt="Thumbnail"
                    />
                    <img
                      v-else
                      src="https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg"
                      class="w-full h-40 object-cover"
                      alt="Thumbnail"
                    />
                    <div
                      class="absolute top-2 right-2 inline-flex items-center gap-1 bg-app-primary text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        class="w-3 h-3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      Completed
                    </div>
                  </div>

                  <div class="flex flex-col flex-1 p-4 gap-3">
                    <h4 class="text-sm font-semibold text-untitled-gray-800 leading-snug">
                      <a
                        href="#"
                        @click.prevent="goToUnit(course.first_unit_id)"
                        class="hover:text-app-primary transition-colors"
                      >
                        {{ course.name }}</a
                      >
                    </h4>

                    <p
                      v-if="course.completed_at"
                      class="inline-flex items-center gap-1.5 text-xs text-untitled-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-3.5 h-3.5 text-untitled-gray-400"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                        />
                      </svg>
                      Completed {{ new Date(course.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }}
                    </p>

                    <div
                      class="mt-auto pt-3 border-t border-untitled-gray-100 flex items-center justify-end"
                    >
                      <button
                        @click="goToUnit(course.first_unit_id)"
                        type="button"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-3 h-3"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                          />
                        </svg>
                        View Course
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-untitled-gray-400"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="w-10 h-10"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                  />
                </svg>
                <p class="mt-3 text-sm font-medium text-untitled-gray-500">
                  No completed courses found.
                </p>
              </div>
            </div>
          </div>

          <!-- Playlist Tab -->
          <div :class="{ hidden: openTab !== 4, block: openTab === 4 }">
            <LoadingState v-if="loadingContent" class="mx-auto text-center py-12" />
            <div v-else>
              <ul
                v-if="filteredPlaylist.length > 0"
                role="list"
                class="grid lg:grid-cols-2 xl:grid-cols-3 grid-cols-1 gap-4 max-h-[520px] overflow-y-auto pr-1"
              >
                <li
                  class="flex flex-col rounded-lg border border-untitled-gray-200 bg-white hover:border-app-primary/40 hover:shadow-md transition-all"
                  v-for="playlist in filteredPlaylist"
                  :key="playlist.playlist_id"
                >
                  <div class="relative flex-shrink-0">
                    <img
                      v-if="playlist?.thumbnail"
                      v-lazy="{
                        src: playlist.thumbnail,
                        error:
                          'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
                        loading:
                          'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
                      }"
                      class="w-full h-40 object-cover"
                    />
                    <img
                      v-else
                      src="https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg"
                      class="w-full h-40 object-cover"
                    />
                    <div
                      class="absolute top-2 right-2 inline-flex items-center gap-1 bg-app-primary text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        class="w-3 h-3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      Completed
                    </div>
                  </div>

                  <div class="flex flex-col flex-1 p-4 gap-3">
                    <h4 class="text-sm font-semibold text-untitled-gray-800 leading-snug">
                      <a
                        href="#"
                        @click.prevent="
                          goToUnit(
                            playlist.first_unit_id,
                            playlist.playlist_id,
                            playlist.type
                          )
                        "
                        class="hover:text-app-primary transition-colors"
                        >{{ playlist.name }}</a
                      >
                    </h4>

                    <p
                      v-if="playlist.completed_at"
                      class="inline-flex items-center gap-1.5 text-xs text-untitled-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-3.5 h-3.5 text-untitled-gray-400"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                        />
                      </svg>
                      Completed {{ new Date(playlist.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }}
                    </p>

                    <div
                      class="mt-auto pt-3 border-t border-untitled-gray-100 flex items-center justify-end"
                    >
                      <button
                        @click="
                          goToUnit(
                            playlist.first_unit_id,
                            playlist.playlist_id,
                            playlist.type
                          )
                        "
                        type="button"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-3 h-3"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                          />
                        </svg>
                        View Playlist
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-untitled-gray-400"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="w-10 h-10"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
                  />
                </svg>
                <p class="mt-3 text-sm font-medium text-untitled-gray-500">
                  No completed playlists found.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </GlobalDialog>
</template>

<style scoped >
ul::-webkit-scrollbar {
  width: 6px;
}

ul::-webkit-scrollbar-track {
  background: transparent;
}

ul::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 9999px;
}
</style>
